<script setup lang="ts">
/**
 * Compatibility tab (T07) — static informational content.
 *
 * Summarises which OpenAI SDK surfaces are mirrored 1:1, where the proxy
 * extends the spec (Gonka-specific headers, simulated tool calls, reasoning
 * deltas), and known gotchas SDK users hit.
 */
import { PUBLIC_PROXY_BASE } from '../../api/docs';
import CopyCodeBlock from './CopyCodeBlock.vue';

interface CompatRow {
  feature: string;
  status: 'supported' | 'partial' | 'planned';
  notes: string;
}

const ROWS: CompatRow[] = [
  { feature: 'POST /v1/chat/completions', status: 'supported', notes: 'Streaming + non-streaming, system/user/assistant/tool roles.' },
  { feature: 'GET /v1/models', status: 'supported', notes: 'Returns OpenAI-shaped list with display_name + context_window extensions.' },
  { feature: 'Tool / function calling', status: 'supported', notes: 'Native upstream + simulated path via SIMULATE_TOOL_CALLS for non-tool-native nodes.' },
  { feature: 'Parallel tool calls', status: 'supported', notes: 'Pass parallel_tool_calls: true; aggregated across choices.' },
  { feature: 'Reasoning content (Kimi K2.6 / DeepSeek R1 / o1)', status: 'supported', notes: 'Delivered via delta.reasoning_content (also under delta.thinking for backwards compat).' },
  { feature: 'Image inputs (vision)', status: 'planned', notes: 'Pending upstream node support; tracked separately.' },
  { feature: 'Audio inputs/outputs', status: 'planned', notes: 'Not on the current chain manifest.' },
  { feature: 'POST /v1/embeddings', status: 'planned', notes: 'Embeddings model proposals are queued upstream.' },
  { feature: 'OpenAI Files / Assistants API', status: 'planned', notes: 'Not on roadmap — out of scope for inference proxy.' },
  { feature: 'response_format=json_object', status: 'partial', notes: 'Honored when upstream node supports structured output; otherwise content is enforced by prompt.' },
];

const DROP_IN = `# Existing OpenAI SDK code
client = OpenAI(api_key=OPENAI_API_KEY)

# Switch to Gonka — only base_url and api_key change.
client = OpenAI(
    base_url="${PUBLIC_PROXY_BASE}",
    api_key="sk-your-api-key-here",
)`;

function statusClass(s: CompatRow['status']): string {
  switch (s) {
    case 'supported':
      return 'bg-surface-soft text-ok';
    case 'partial':
      return 'pill';
    case 'planned':
      return 'bg-code text-muted';
  }
}

function statusLabel(s: CompatRow['status']): string {
  switch (s) {
    case 'supported':
      return 'Supported';
    case 'partial':
      return 'Partial';
    case 'planned':
      return 'Planned';
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <section class="card p-6">
      <h2 class="text-lg font-semibold">OpenAI Compatibility</h2>
      <p class="text-sm text-muted mt-2">
        The proxy mirrors the OpenAI API at <code>/v1/*</code>. In most cases you can keep your
        existing client library and only swap the base URL and API key.
      </p>
      <div class="mt-4">
        <CopyCodeBlock label="Python — switching from OpenAI to Gonka" :code="DROP_IN" language="python" />
      </div>
    </section>

    <section class="card p-6">
      <h2 class="text-lg font-semibold">Feature matrix</h2>
      <div class="mt-4 overflow-x-auto">
        <table class="table w-full">
          <thead>
            <tr>
              <th>Feature</th>
              <th class="w-[140px]">Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in ROWS" :key="row.feature">
              <td class="text-sm">{{ row.feature }}</td>
              <td>
                <span
                  :class="['inline-block text-[11px] px-2 py-0.5 rounded', statusClass(row.status)]"
                >{{ statusLabel(row.status) }}</span>
              </td>
              <td class="text-sm text-muted">{{ row.notes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="card p-6">
      <h2 class="text-lg font-semibold">Known differences from OpenAI</h2>
      <ul class="mt-2 list-disc list-inside text-sm text-muted space-y-1">
        <li>API keys use the <code>gnk-sk-</code> prefix, not OpenAI's <code>sk-</code>; both
          forms are accepted in the <code>Authorization</code> header.</li>
        <li>Model IDs are the upstream HuggingFace repo path (e.g.
          <code>Qwen/Qwen3-235B-A22B-Instruct-2507-FP8</code>), not OpenAI aliases like
          <code>gpt-4o</code>.</li>
        <li>Streaming chunks may include a <code>delta.reasoning_content</code> field for models
          that emit explicit chain-of-thought (Kimi, DeepSeek R1, o1-style).</li>
        <li>Rate-limit headers follow the <code>X-RateLimit-*</code> de-facto convention; OpenAI
          ships <code>retry-after</code> on 429 — we ship both.</li>
        <li>Some response usage fields include <code>upstream_provider</code> identifying which
          decentralized node served the request (useful for debugging and SLO tracking).</li>
      </ul>
    </section>
  </div>
</template>
